/* How the wallet orders cards. Flow E, gap 29.
 *
 * THE GAP, QUOTED: "Closest to a reward needs a formula. Two stamps from a
 * free coffee against sixty points from a hotel night are not comparable, and
 * distance has to weigh in somewhere."
 *
 * WHAT MAKES THEM COMPARABLE, AND WHAT DOES NOT.
 *
 * `threshold - balance` is the obvious answer and it is wrong. It is measured
 * in stamps on one card and points on the other, so a coffee card six stamps
 * from a reward outranks a hotel card ten points from one, and ten points
 * might be a single stay.
 *
 * The fraction completed IS comparable, because it is a ratio and carries no
 * unit. 6 of 8 and 75 of 100 are both three quarters of the way there, and a
 * person reading their wallet agrees they are equally close. So that is the
 * key.
 *
 * WHAT WE DELIBERATELY DO NOT DO. The truest measure is visits remaining, and
 * for a stamp card we know it exactly: a stamp is a visit. For a points card
 * we cannot know it, because points per visit varies by what the person buys
 * and we do not collect an earn rate. Converting points to visits would mean
 * inventing that rate, and a wallet ordered by an invented number is worse
 * than one ordered by an honest ratio. If the earn rate is ever collected this
 * is the function that changes.
 *
 * PROXIMITY IS NOT IN HERE. The original formula weights distance. This app
 * never asks for a location, and there are no merchant coordinates to measure
 * against, so there is nothing to weigh. Left out rather than stubbed, so that
 * nobody reads a `distance` term and assumes it is doing something.
 *
 * DORMANCY IS NOT IN HERE EITHER. Gap 30 asks whether quiet cards archive and
 * how a person gets them back, and that is an unmade policy decision, recorded
 * as deferred in backend/NOTES.md. Ordering a quiet card lower would be a
 * quiet way of making that decision without making it. Recency only ever
 * breaks a tie here.
 */

/** The fields ranking needs. Deliberately narrower than the wallet's `Card`,
 *  so this can be tested without constructing a whole membership. */
export interface RankableCard {
  /** Rewards earned and not yet taken. */
  claimable: number;
  /** Progress toward the next reward, in stamps or in points. */
  balance: number;
  /** What the next reward costs, in the same unit as `balance`. */
  threshold: number;
  /** ISO timestamp of the last earn, or null for a card never used. */
  last_earn_at: string | null;
}

/** How far along a card is, from 0 to 1.
 *
 * A threshold of zero cannot be divided by, and it is a real value: a
 * misconfigured programme, or a card whose tier has no requirement. It scores
 * as nothing rather than as infinity or a crash. */
export function progress(card: RankableCard): number {
  if (!(card.threshold > 0)) return 0;
  // Clamped, because a balance above the threshold happens between earning the
  // last stamp and the reward being issued, and an unclamped value would sort
  // that card above one that is genuinely finished.
  return Math.min(1, Math.max(0, card.balance / card.threshold));
}

/** The wallet's order: what you can take now, then what you are closest to.
 *
 * Returns a new array. The input is not mutated, because callers hold the
 * server's list and re-sorting it in place makes a later render depend on how
 * many times this ran.
 *
 * The comparison is total and deterministic. Two cards identical on every key
 * fall back to their membership order from the server, which is stable, so a
 * re-render never reshuffles equal cards in front of somebody reading them.
 */
export function rankCards<T extends RankableCard>(cards: readonly T[]): T[] {
  return [...cards].sort((a, b) => {
    // 1. A reward you can take now beats any amount of progress toward one.
    //    Ordered by how many, because two free coffees is a stronger reason to
    //    walk in than one.
    if (a.claimable !== b.claimable) return b.claimable - a.claimable;

    // 2. Closest to the next reward, as a fraction. See the note above for why
    //    this is a ratio and not a count.
    const pa = progress(a);
    const pb = progress(b);
    if (pa !== pb) return pb - pa;

    // 3. Most recently used, to break a tie. Not a ranking signal of its own:
    //    a card you used yesterday and a card you used in March are equally
    //    far from their rewards if they are equally far from their rewards.
    //    A card never used sorts last among its ties, which is right: it is
    //    the one you have the least reason to open.
    const la = a.last_earn_at ?? "";
    const lb = b.last_earn_at ?? "";
    if (la !== lb) return lb.localeCompare(la);

    return 0;
  });
}

/** The wallet's two groups, in one pass, so a screen cannot render them from
 *  differently-ordered lists. */
export function walletSections<T extends RankableCard>(
  cards: readonly T[],
): { claimable: T[]; inProgress: T[] } {
  const ranked = rankCards(cards);
  return {
    claimable: ranked.filter((c) => c.claimable > 0),
    inProgress: ranked.filter((c) => c.claimable === 0),
  };
}
